/* NaborFolderPanel — obsah složky NÁBOROVÉHO kontaktu.
 *
 * PROČ EXISTUJE: klik na kandidáta v Klientech otevíral obyčejnou klientskou složku —
 * Smlouvy, Obchody, Příležitosti, Analýza. U náboru nedávají smysl a nikde nebylo
 * vidět, že jde o nábor. Tenhle panel ukazuje náborové věci: fázi, co o něm víme
 * z registru ČNB, další krok, poznámky a historii oslovení.
 *
 * Data jsou tatáž jako v /nabor (is_recruit + recruit_* pole na kontaktu) — žádná
 * druhá pravda. Podrobná čísla (produkce, kmen, síť) a oslovovací sekvence zůstávají
 * v pipeline; odsud tam vede tlačítko.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Calendar, ArrowRight, History, Save, ExternalLink, Info, ShieldOff } from "lucide-react";
import api from "@/lib/api";
import CnbFakta from "@/components/nabor/CnbFakta";
import { NABOR_FAZE, NABOR_FAZE_KONEC, naborStageLabel, naborStageColor } from "@/lib/naborFaze";

const HIST_TYPE_LABEL = {
  cs: { telefonat: "Telefon", schuzka: "Schůzka", ukol: "Úkol", email: "E-mail", udalost: "Událost", dopis: "Dopis" },
  sk: { telefonat: "Telefón", schuzka: "Stretnutie", ukol: "Úloha", email: "E-mail", udalost: "Udalosť", dopis: "List" },
};
const HIST_TYPE_COLOR = { telefonat: "#34C759", schuzka: "#007AFF", ukol: "#FF9500", email: "#5AC8FA", udalost: "#AF52DE", dopis: "#8E8E93" };

const T = {
  cs: {
    eye: "Náborová složka", intro: "Tohle není klient, ale kandidát do týmu. Klientské záložky (smlouvy, obchody, analýza) u něj nemají co dělat.",
    phase: "Fáze náboru", phaseSaved: "Fáze změněna", phaseFail: "Fázi se nepodařilo změnit",
    next: "Další krok — kdy se ozvat", nextSaved: "Termín uložen", nextFail: "Termín se nepodařilo uložit", noNext: "Bez termínu — domluv si další krok, jinak kandidát vyšumí.",
    notes: "Co o něm vím", notesPh: "Co ho štve, co ho drží, co jsem mu slíbil…",
    notesHelp: "Klikni a doplň — z těchhle vět se pak staví nabídka:",
    prompts: ["Co ho štve:", "Co ho drží:", "Co mu chybí u klienta:", "Kde se vidí za 3 roky:", "Co jsem slíbil:", "Co musí vidět, aby kývl:"],
    save: "Uložit poznámku", saved: "Poznámka uložena", saveFail: "Poznámku se nepodařilo uložit", saving: "Ukládám…",
    hist: "Historie oslovení", histEmpty: "Zatím žádná aktivita. Naplánuj další krok v náborové pipeline — objeví se tady i v diáři.", histLoading: "Načítám…", histFail: "Historii se nepodařilo načíst.", histDone: "hotovo",
    openPipe: "Otevřít v náborové pipeline", openPipeSub: "Tam jsou jeho čísla, oslovovací sekvence, playbook a nabídka.",
    namitka: "Vznesl námitku — nekontaktovat", namitkaPotvrd: "Zapsat námitku? Kandidát půjde do koše a už ho nikdo v aplikaci neuvidí v mapě ani nepřidá do náboru.",
    namitkaOk: "Námitka zapsána — kandidát je v koši a na seznamu nekontaktovat", namitkaFail: "Námitku se nepodařilo zapsat",
  },
  sk: {
    eye: "Náborová zložka", intro: "Toto nie je klient, ale kandidát do tímu. Klientske záložky (zmluvy, obchody, analýza) u neho nemajú čo robiť.",
    phase: "Fáza náboru", phaseSaved: "Fáza zmenená", phaseFail: "Fázu sa nepodarilo zmeniť",
    next: "Ďalší krok — kedy sa ozvať", nextSaved: "Termín uložený", nextFail: "Termín sa nepodarilo uložiť", noNext: "Bez termínu — dohodni si ďalší krok, inak kandidát vyšumí.",
    notes: "Čo o ňom viem", notesPh: "Čo ho štve, čo ho drží, čo som mu sľúbil…",
    notesHelp: "Klikni a doplň — z týchto viet sa potom stavia ponuka:",
    prompts: ["Čo ho štve:", "Čo ho drží:", "Čo mu chýba u klienta:", "Kde sa vidí o 3 roky:", "Čo som sľúbil:", "Čo musí vidieť, aby kývol:"],
    save: "Uložiť poznámku", saved: "Poznámka uložená", saveFail: "Poznámku sa nepodarilo uložiť", saving: "Ukladám…",
    hist: "História oslovenia", histEmpty: "Zatiaľ žiadna aktivita. Naplánuj ďalší krok v náborovej pipeline — objaví sa tu aj v diári.", histLoading: "Načítavam…", histFail: "Históriu sa nepodarilo načítať.", histDone: "hotovo",
    openPipe: "Otvoriť v náborovej pipeline", openPipeSub: "Tam sú jeho čísla, oslovovacia sekvencia, playbook a ponuka.",
    namitka: "Namietal — nekontaktovať", namitkaPotvrd: "Zapísať námietku? Kandidát pôjde do koša a už ho nikto v aplikácii neuvidí v mape ani nepridá do náboru.",
    namitkaOk: "Námietka zapísaná — kandidát je v koši a na zozname nekontaktovať", namitkaFail: "Námietku sa nepodarilo zapísať",
  },
};

export default function NaborFolderPanel({ contact, onPatch, lang = "cs" }) {
  const L = lang === "sk" ? "sk" : "cs";
  const t = T[L];
  const navigate = useNavigate();
  const meta = contact?.recruit_meta || {};
  const [stage, setStage] = useState(contact?.recruit_stage || "tip");
  const [next, setNext] = useState(contact?.recruit_next_contact || "");
  const [notes, setNotes] = useState(contact?.recruit_notes || "");
  const [savingNotes, setSavingNotes] = useState(false);
  const [activities, setActivities] = useState(null);   // null = načítám

  const zapsatNamitku = async () => {
    if (!contact?.id || !window.confirm(t.namitkaPotvrd)) return;
    try {
      await api.post("/api/nabor/nekontaktovat", { contact_id: contact.id, zeme: meta.zeme || "" });
      toast.success(t.namitkaOk);
      navigate("/nabor");
    } catch (e) { toast.error(e?.response?.data?.detail || t.namitkaFail); }
  };
  const [histFailed, setHistFailed] = useState(false);  // chyba ≠ prázdná historie

  useEffect(() => {
    setStage(contact?.recruit_stage || "tip");
    setNext(contact?.recruit_next_contact || "");
    setNotes(contact?.recruit_notes || "");
  }, [contact?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/api/crm/tasks", { params: { contact_id: contact?.id } });
        const arr = Array.isArray(data) ? data : [];
        arr.sort((a, b) => new Date(b.deadline || b.created_at || 0) - new Date(a.deadline || a.created_at || 0));
        if (!cancelled) { setActivities(arr); setHistFailed(false); }
      } catch {
        if (!cancelled) { setActivities([]); setHistFailed(true); }
      }
    })();
    return () => { cancelled = true; };
  }, [contact?.id]);

  const patch = async (body, okMsg, failMsg) => {
    try { await onPatch?.(body); if (okMsg) toast.success(okMsg); return true; }
    catch { toast.error(failMsg); return false; }
  };

  const changeStage = async (id) => {
    const prev = stage;
    setStage(id);
    const ok = await patch({ recruit_stage: id }, t.phaseSaved, t.phaseFail);
    if (!ok) setStage(prev);   // neuložilo se → nelži, vrať původní fázi
  };

  const changeNext = async (v) => {
    const prev = next;
    setNext(v);
    const ok = await patch({ recruit_next_contact: v || null }, t.nextSaved, t.nextFail);
    if (!ok) setNext(prev);
  };

  const saveNotes = async () => {
    setSavingNotes(true);
    await patch({ recruit_notes: notes }, t.saved, t.saveFail);
    setSavingNotes(false);
  };

  const histLabel = (at) => HIST_TYPE_LABEL[L][at] || (L === "sk" ? "Aktivita" : "Aktivita");
  const koncove = Object.keys(NABOR_FAZE_KONEC);

  return (
    <div className="px-4 md:px-5 py-3 space-y-3" data-testid="tab-nabor">
      {/* Proč vypadá složka jinak */}
      <div className="rounded-xl px-3 py-2.5 flex items-start gap-2" style={{ background: "rgba(109,72,184,0.08)", border: "1px solid rgba(109,72,184,0.35)" }} data-testid="nabor-folder-intro">
        <Info size={13} style={{ color: "#6D48B8", flexShrink: 0, marginTop: 2 }} />
        <div>
          <div className="text-[10px] uppercase tracking-[0.16em] font-bold" style={{ color: "#6D48B8" }}>{t.eye}</div>
          <div className="text-[12px] leading-snug mt-0.5" style={{ color: "var(--ink)" }}>{t.intro}</div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="space-y-3">
          {/* Fáze náboru */}
          <div>
            <div className="text-[10.5px] uppercase tracking-wider font-bold mb-1.5" style={{ color: "var(--muted)" }}>{t.phase}</div>
            <div className="flex flex-wrap gap-1.5" data-testid="nabor-folder-faze">
              {[...NABOR_FAZE.map((f) => f.id), ...koncove].map((id) => {
                const on = id === stage;
                const color = naborStageColor(id);
                return (
                  <button key={id} onClick={() => changeStage(id)} data-testid={`nabor-folder-faze-${id}`}
                    className="px-2.5 py-1 rounded-full text-[11.5px] font-semibold transition-colors"
                    style={{ background: on ? color : "var(--surface)", color: on ? "#FFFFFF" : "var(--muted)", border: `1px solid ${on ? color : "var(--line)"}` }}>
                    {naborStageLabel(id, L)}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Další krok */}
          <div>
            <div className="text-[10.5px] uppercase tracking-wider font-bold mb-1.5 flex items-center gap-1.5" style={{ color: "var(--muted)" }}><Calendar size={11} /> {t.next}</div>
            <input type="date" value={next || ""} onChange={(e) => changeNext(e.target.value)} data-testid="nabor-folder-next"
              className="w-full rounded-lg px-2.5 py-2 text-sm outline-none" style={{ border: "1px solid var(--line)", background: "var(--bg)", color: "var(--ink)" }} />
            {!next && <div className="text-[11px] mt-1" style={{ color: "var(--accent)" }}>{t.noNext}</div>}
          </div>

          {/* Co víme z ČNB */}
          <CnbFakta meta={meta} L={L} />

          <button onClick={() => navigate("/nabor")} data-testid="nabor-folder-open-pipe"
            className="w-full rounded-xl px-3 py-2.5 text-left transition-transform hover:-translate-y-[1px]"
            style={{ background: "rgba(109,72,184,0.10)", border: "1px solid rgba(109,72,184,0.45)" }}>
            <div className="flex items-center gap-1.5 text-[12.5px] font-bold" style={{ color: "#5B37A6" }}>
              <ExternalLink size={13} /> {t.openPipe} <ArrowRight size={13} className="ml-auto opacity-60" />
            </div>
            <div className="text-[11px] mt-0.5" style={{ color: "var(--muted)" }}>{t.openPipeSub}</div>
          </button>

          {/* Námitka čl. 21 GDPR → seznam „nekontaktovat“ + koš (F10c, 19. 10. 2026) */}
          <button onClick={zapsatNamitku} data-testid="nabor-folder-namitka"
            className="w-full rounded-xl px-3 min-h-[44px] text-left text-[12.5px] font-semibold flex items-center gap-1.5"
            style={{ background: "var(--surface)", border: "1px solid var(--line)", color: "var(--ink)" }}>
            <ShieldOff size={13} aria-hidden="true" /> {t.namitka}
          </button>
        </div>

        <div className="space-y-3">
          {/* Poznámka o něm */}
          <div>
            <div className="text-[10.5px] uppercase tracking-wider font-bold mb-1.5" style={{ color: "var(--muted)" }}>{t.notes}</div>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={6} placeholder={t.notesPh} data-testid="nabor-folder-notes"
              className="w-full rounded-lg px-2.5 py-2 text-sm outline-none resize-y" style={{ border: "1px solid var(--line)", background: "var(--bg)", color: "var(--ink)" }} />
            <div className="text-[11px] mt-1.5 mb-1" style={{ color: "var(--muted)" }}>{t.notesHelp}</div>
            <div className="flex flex-wrap gap-1.5" data-testid="nabor-folder-prompts">
              {t.prompts.map((p, i) => (
                <button key={p} onClick={() => setNotes((v) => (v && !v.endsWith("\n") ? v + "\n" : v) + p + " ")} data-testid={`nabor-folder-prompt-${i}`}
                  className="px-2 py-1 rounded-full text-[11px] font-semibold" style={{ background: "var(--bg)", color: "var(--ink)", border: "1px dashed var(--line)" }}>
                  + {p}
                </button>
              ))}
            </div>
            <button onClick={saveNotes} disabled={savingNotes} data-testid="nabor-folder-save-notes"
              className="mt-2 inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-[12px] font-bold disabled:opacity-50"
              style={{ background: "var(--accent)", color: "#FFFFFF" }}>
              <Save size={13} /> {savingNotes ? t.saving : t.save}
            </button>
          </div>

          {/* Historie oslovení */}
          <div>
            <div className="text-[10.5px] uppercase tracking-wider font-bold mb-1.5 flex items-center gap-1.5" style={{ color: "var(--muted)" }}><History size={11} /> {t.hist}</div>
            {activities === null ? (
              <div className="text-[12px] py-2" style={{ color: "var(--muted)" }}>{t.histLoading}</div>
            ) : histFailed ? (
              <div className="text-[12px] py-2" style={{ color: "var(--accent)" }} data-testid="nabor-folder-hist-error">{t.histFail}</div>
            ) : activities.length === 0 ? (
              <div className="text-[12px] py-2 leading-relaxed" style={{ color: "var(--muted)" }} data-testid="nabor-folder-hist-empty">{t.histEmpty}</div>
            ) : (
              <div className="relative pl-4 space-y-2.5" data-testid="nabor-folder-hist">
                <div className="absolute top-1.5 bottom-1.5 left-[5px] w-px" style={{ background: "var(--line)" }} />
                {activities.map((a) => {
                  const at = a.activity_type || "ukol";
                  const color = HIST_TYPE_COLOR[at] || "#8E8E93";
                  const d = a.deadline || a.created_at;
                  const dt = d ? new Date(d) : null;
                  const dStr = dt && !isNaN(dt.getTime())
                    ? dt.toLocaleDateString(L === "sk" ? "sk-SK" : "cs-CZ", { day: "numeric", month: "short" })
                    : "";
                  return (
                    <div key={a.id} className="relative">
                      <span className="absolute -left-4 top-1" style={{ width: 11, height: 11, borderRadius: "50%", background: color, boxShadow: "0 0 0 3px var(--bg)" }} />
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="text-[9px] uppercase font-bold px-1.5 py-0.5 rounded" style={{ background: `${color}1A`, color }}>{histLabel(at)}</span>
                        {dStr && <span className="text-[10.5px] tabular-nums" style={{ color: "var(--muted)" }}>{dStr}</span>}
                        {a.done && <span className="text-[10px] font-semibold" style={{ color: "var(--money-dark, #0AA653)" }}>✓ {t.histDone}</span>}
                      </div>
                      <div className="text-[12.5px] leading-snug mt-0.5" style={{ color: "var(--ink)" }}>{a.text || a.agenda || histLabel(at)}</div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
